import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccountAccessTrustIndicatorsMapper from 'components/Account/AccountAccess/AccountAccessTrustIndicatorsMapper'
import LineGlassMartiniAltIcon from 'LuxKit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import AccountAccessUserInput from 'components/Account/AccountAccess/AccountAccessUserInput'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import TextButton from 'LuxKit/Button/TextButton'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import ReCAPTCHA from 'react-google-recaptcha'
import { getUserKeyFromPhone, checkUserExists, clearAuthError, loginApple } from 'actions/AuthActions'
import { formToObject } from 'lib/forms/formToObject'
import { LoginForm } from 'components/Account/AccountAccess/AccountAccessLoginStart'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingIndicator from './Loading/LoadingIndicator'
import { getLastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  margin-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    margin-bottom: ${rem(40)};
  }
`

const Or = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
    display: block;
  }
`

const InputGroup = styled(Group)`
  padding-bottom: ${rem(24)};
`

const StyledAccountAccessUserInput = styled(AccountAccessUserInput)`
  flex-grow: 1;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  position: relative;
`

interface Props {
  offerType: string
  saveUpToPercent?: number
}

function OfferPageSignUp({ offerType, saveUpToPercent }: Props) {
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const [userId, setUserId] = useState<string>('')
  const [applePrivateEmailData, setApplePrivateEmailData] = useState<App.AppleLoginData | null>(null)
  const processing = useAppSelector(state => state.auth.processing)
  const user = useAppSelector(state => state.auth.users[userId])
  const error = useAppSelector(state => state.auth.error)

  const croMandatoryPhoneVariant = useOptimizelyExperiment(OptimizelyExperiments.croMandatoryPhoneNumberOnSignUp)
  const isPhoneFirstVariant = croMandatoryPhoneVariant === 'variant_2'
  const userInputMode = isPhoneFirstVariant ? 'phone' : 'emailOrPhone'
  const lastUsedLoginMethod = getLastUsedLoginMethod()

  const reRef = useRef<ReCAPTCHA>(null)

  const isStoreMode = useAppSelector(state => state.system.storeMode)
  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled

  const onPrivateAppleEmailLogin = useCallback((appleLoginData: App.AppleLoginData, email: string) => {
    setUserId(email)
    setApplePrivateEmailData(appleLoginData)
    dispatch(checkUserExists({ email }, reRef.current ?? undefined))
  }, [dispatch, setApplePrivateEmailData])

  const onCheckEmail = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const loginData = formToObject<LoginForm>(event.currentTarget)
    const userId = loginData.phoneNumber ? getUserKeyFromPhone(loginData.phoneNumber) : loginData.email
    setUserId(userId ?? '')
    dispatch(checkUserExists(loginData, reRef.current ?? undefined))
  }, [dispatch])

  const openAccountModal = useCallback((initialMode: App.UiAccountModalMode, defaultEmail?: string, defaultAppleData?: App.AppleLoginData | null) => {
    showModal(<AccountAccessModal
      initialMode={initialMode}
      defaultEmail={defaultEmail}
      defaultAppleData={defaultAppleData ?? undefined}
    />)
  }, [showModal])

  useEffect(() => {
    if (!processing && user) {
      if (user.exists) {
        if (applePrivateEmailData) {
          dispatch(loginApple(applePrivateEmailData))
        } else {
          openAccountModal(user.maskedPhone ? 'loginPasswordOTP' : 'loginPassword', userId)
        }
      } else {
        if (applePrivateEmailData && user.id?.endsWith('@privaterelay.appleid.com')) {
          openAccountModal('applePrivateEmail', userId, applePrivateEmailData)
        } else {
          openAccountModal('join', userId)
        }
      }
    }
  }, [user, processing, openAccountModal, userId, dispatch, applePrivateEmailData])

  useEffect(() => {
    dispatch(clearAuthError())
  }, [dispatch])

  const onShowEmailEntry = useCallback(() => {
    dispatch(clearAuthError())
    openAccountModal('loginEmail')
  }, [dispatch, openAccountModal])

  const emailConflictError = typeof error?.message === 'string' && error.message.includes('Please log in with your email')
  const errorBannerContent = emailConflictError ? (
    <span>
      There are multiple accounts with your phone number.{' '}
      <TextButton kind="tertiary" size="small" onClick={onShowEmailEntry}>
        Click here to log in with your email instead.
      </TextButton>
    </span>
  ) : error?.message

  return (
    <Container>
      <VerticalSpacer gap={20}>
        <Group direction="vertical" gap={8}>
          <HeadingTextBlock variant="heading2">
            {saveUpToPercent && saveUpToPercent > 0 && <>
              Sign up and <em>save</em> up to {saveUpToPercent}% on this {offerType}
            </>}
            {!saveUpToPercent && <>
              Sign up for free to <em>unlock</em> this {offerType}
            </>}
          </HeadingTextBlock>
          <BodyTextBlock variant="medium">
            Join over 8 million global members to unlock our amazing deals & benefits!
          </BodyTextBlock>
        </Group>
        <VerticalSpacer gap={4}>
          <BodyTextBlock variant="medium" startIcon={<LineGlassMartiniAltIcon colour="highlight-secondary" />}>
            <b>More inclusions:</b> Drinks, dining, transfers & more
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineGlobeAltIcon colour="highlight-secondary" />}>
            <b>More options:</b> 1,000s of hotels, tours & flights
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LinePhoneIcon colour="highlight-secondary" />}>
            <b>More expertise:</b> 24/7 customer service, worldwide
          </BodyTextBlock>
        </VerticalSpacer>
        <Group direction="vertical" horizontalAlign="start">
          <AccountAccessTrustIndicatorsMapper />
        </Group>
      </VerticalSpacer>
      <StyledVerticalSpacer gap={20} as="form" onSubmit={onCheckEmail}>
        {errorBannerContent && <MessageBanner kind="critical">{errorBannerContent}</MessageBanner>}
        <InputGroup
          direction="vertical"
          desktopDirection="horizontal"
          gap={48}
          verticalAlign="end"
        >
          <StyledAccountAccessUserInput
            noValidationSpacing
            autoFocus={false}
            inputMode={userInputMode}
            lastUsedLoginMethod={lastUsedLoginMethod}
          />
          <TextButton
            kind="primary"
            size="large"
            type="submit"
          >
            Unlock this offer
          </TextButton>
          <LoadingIndicator
            visible={!!processing}
            floating
            backdrop="white"
          />
        </InputGroup>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        {showSocialMediaButtons && (
          <>
            <Or>
              <BodyText colour="neutral-two" variant="medium">
                Other ways to log in
              </BodyText>
            </Or>
            <SocialButtons
              mode="login"
              inline
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
              lastUsedLoginMethod={lastUsedLoginMethod}
              onEmailEntryClick={isPhoneFirstVariant ? onShowEmailEntry : undefined}
              gridOnMobile={isPhoneFirstVariant}
            />
          </>
        )}
        <AccountAccessLegalText />
      </StyledVerticalSpacer>
    </Container>
  )
}

export default OfferPageSignUp
